/* global ResizeObserver */
import { Controller } from '@hotwired/stimulus';
import Highcharts from 'highcharts-v12';
import 'highcharts-v12/highcharts-more';
import 'highcharts-v12/modules/solid-gauge';

// Renders whatever Highcharts config it is handed. Callers build that config
// however they like; /ikiks gets it from the trevl gem via Kiks::Visualization.
export default class extends Controller<HTMLElement> {
  static values = { config: Object };

  declare configValue: Highcharts.Options;

  private chart?: Highcharts.Chart;
  private observer?: ResizeObserver;

  private reflow = (): void => {
    this.chart?.reflow();
  };

  private scheduleReflow = (): void => {
    window.requestAnimationFrame(this.reflow);
  };

  // Firefox lays the page out for paper while the print dialogue is open and is
  // still in that layout when afterprint fires, so reflowing straight away
  // measures the paper width and writes it into the chart for good: the bars
  // then hang out of their card until the next resize. Waiting for the screen
  // layout to be back is what makes cancelling the dialogue harmless. The
  // ResizeObserver below does not catch this — the element itself never
  // changes size on screen, only the chart inside it does.
  private reflowAfterPrint = (): void => {
    window.requestAnimationFrame(this.scheduleReflow);
  };

  connect(): void {
    this.chart = Highcharts.chart(this.element, this.configValue);

    // A chart sizes itself to its container. The print stylesheet gives that
    // container a different width, so without a reflow the bars would print at
    // their screen width and overflow the page.
    window.addEventListener('beforeprint', this.reflow);
    window.addEventListener('afterprint', this.reflowAfterPrint);

    // Highcharts writes a fixed width into the element it renders into, so its
    // own window-resize handler compares that width against itself and never
    // sees a change. Watching the element is what makes a chart follow a
    // rotated phone. The container also needs min-width 0, or flex refuses to
    // shrink it below that written width in the first place.
    this.observer = new ResizeObserver(this.scheduleReflow);
    this.observer.observe(this.element);
  }

  disconnect(): void {
    window.removeEventListener('beforeprint', this.reflow);
    window.removeEventListener('afterprint', this.reflowAfterPrint);
    this.observer?.disconnect();
    this.observer = undefined;
    this.chart?.destroy();
    this.chart = undefined;
  }
}
